import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  emailCampaignLocations,
  locationWiseEmailCampaignReportData,
  emailCampaignReportData,
} from "@/features/campaigns/email/utils/emailCampaignsDummyData";
import LocationFilter from "../common/LocationFilter";
import SwitchTabs from "@/components/tabs/SwitchTabs";
import EmailCampaignCountDisplay from "./EmailCampaignCountDisplay";
import EmailCampaignReportTable from "./EmailCampaignReportTable";
import { Typography } from "@mui/material";

type EmailCampaignReportDetailProps = {
  selectedLocation?: string | number | null;
};

type EmailLocation = {
  id: string | number;
  name: string;
};

type LocationReportSummary = {
  emailsSent?: number;
  delivered?: number;
  uniqueOpens?: number;
  uniqueClicks?: number;
  conversions?: number;
  bounces?: number;
};

type LocationCampaignReport = {
  id: number;
  locationName: string;
  sendDate: string;
  status: string;
  customerList: string;
  template: string;
  emailsSent: number;
  delivered: number;
  uniqueOpens: number;
  conversions: number;
  bounces: number;
  image: string;
};

type LocationWiseEmailCampaignReport = {
  summary: LocationReportSummary;
  campaigns: LocationCampaignReport[];
};

const typedEmailCampaignLocations = emailCampaignLocations as EmailLocation[];

const typedLocationWiseEmailCampaignReportData =
  locationWiseEmailCampaignReportData as Record<
    string,
    LocationWiseEmailCampaignReport | undefined
  >;

function EmailCampaignReportDetail({
  selectedLocation: initialSelectedLocation,
}: EmailCampaignReportDetailProps) {
  const { campaignId } = useParams();
  const [selectedLocation, setSelectedLocation] = useState(
    initialSelectedLocation || typedEmailCampaignLocations[0]?.id
  );
  const [selectedTab, setSelectedTab] = useState(0);

  // Get campaign name from campaign ID
  const campaignName = useMemo(() => {
    const campaign = emailCampaignReportData.find(
      (campaign) => campaign.id === parseInt(campaignId ?? "", 10)
    );
    return campaign?.name || "Late Night";
  }, [campaignId]);

  // Update selectedLocation when initialSelectedLocation prop changes
  useEffect(() => {
    if (initialSelectedLocation) {
      setSelectedLocation(initialSelectedLocation);
    }
  }, [initialSelectedLocation]);

  const handleLocationChange = (location: EmailLocation | null) => {
    setSelectedLocation(location?.id);
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };

  // Get current location data
  const currentLocationData = useMemo(() => {
    const locationKey = typedEmailCampaignLocations.find(
      (loc) => loc.id === selectedLocation
    )?.name;
    return locationKey
      ? typedLocationWiseEmailCampaignReportData[locationKey]
      : undefined;
  }, [selectedLocation]);

  return (
    <div className="container-fluid">
      {/* Header Section */}
      <div className="row mb-2">
        <div className="col-12 col-md-4 mb-3 mb-md-0">
          <Typography variant="h5" className="widget-title mb-0">
            {campaignName} Email Campaign Report
          </Typography>
        </div>
        <div className="col-12 col-md-8 mt-3 mt-md-0 p-md-0">
          <div className="d-flex flex-column flex-sm-row justify-content-start justify-content-xl-end  align-items-start gap-3">
            <LocationFilter
              locations={typedEmailCampaignLocations}
              selectedLocation={selectedLocation}
              onLocationChange={handleLocationChange}
              // label="Select Location"
              className="p-0"
            />
            <SwitchTabs
              tabs={["#", "%"]}
              selectedTab={selectedTab}
              onChange={handleTabChange}
              styleType={1}
              className="email-campaign-tabs m-0"
            />
          </div>
        </div>
      </div>

      {/* Count Display */}
      {currentLocationData && (
        <div className="row">
          <div className="col-12">
            <EmailCampaignCountDisplay
              summaryData={currentLocationData.summary}
              selectedTab={selectedTab}
            />
          </div>
        </div>
      )}

      {/* Campaign Table */}
      {currentLocationData && (
        <div className="row">
          <div className="col-12">
            <EmailCampaignReportTable
              campaignsData={currentLocationData.campaigns}
              selectedTab={selectedTab}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailCampaignReportDetail;
